/**
 * Loan Products Hero Section
 * Fetches data from CMS API
 */
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Home } from 'lucide-react';
import axios from 'axios';

const CMS_API_URL = process.env.REACT_APP_API_BASE_URL_CMS || '';
const LOAN_SECTION_UID = 'LOAN-SECTION';

const LoanProductsHero = () => {
  const [heroData, setHeroData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchHeroData = async () => {
      try {
        const response = await axios.post(
          `${CMS_API_URL}sections/get-by-uid`,
          { UID: LOAN_SECTION_UID },
          {
            headers: { 'Content-Type': 'application/json' },
            timeout: 30000,
          }
        );

        if (response.data?.code === 1 && Array.isArray(response.data?.data) && response.data.data.length > 0) {
          const data = response.data.data[0];
          if (data.IsDeleted === false && data.IsPublished === true) {
            // Parse Description HTML to extract tag, title, and description
            const descHtml = data.Description || '';
            
            // Extract first paragraph as tag (e.g., "Financial Products")
            const tagMatch = descHtml.match(/<p>([^<]+)<\/p>/);
            const tag = tagMatch ? tagMatch[1].replace(/&nbsp;/g, ' ').trim() : '';
            
            // Extract h1 content as title
            const titleMatch = descHtml.match(/<h1[^>]*>(?:<[^>]*>)?([^<]+)/);
            const title = titleMatch ? titleMatch[1].replace(/&nbsp;/g, ' ').trim() : data.Title;
            
            // Extract second paragraph as description
            const allParagraphs = descHtml.match(/<p>([^<]+)<\/p>/g);
            let description = '';
            if (allParagraphs && allParagraphs.length > 1) {
              description = allParagraphs[1]
                .replace(/<p>([^<]+)<\/p>/, '$1')
                .replace(/&nbsp;/g, ' ')
                .trim();
            }

            setHeroData({
              Title: title,
              Tag: tag,
              Description: description,
            });
          }
        }
      } catch (error) {
        console.error('Failed to fetch loan hero data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchHeroData();
  }, []);

  // Return null if no data after loading
  if (!loading && !heroData) {
    return null;
  }

  // Loading skeleton
  if (loading) {
    return (
      <section className="bg-[#2f3951] py-5 md:py-5">
        <div className="container mx-auto px-6 md:px-8 lg:px-12">
          <div className="h-4 w-32 bg-white/20 rounded mb-3 animate-pulse" />
          <div className="h-4 w-24 bg-[#ca9a37]/50 rounded mb-3 animate-pulse" />
          <div className="h-8 w-48 bg-white/20 rounded mb-2 animate-pulse" />
          <div className="w-24 h-1 bg-white/20 mb-3 animate-pulse" />
          <div className="h-4 w-96 bg-white/20 rounded animate-pulse" />
        </div>
      </section>
    );
  }

  return (
    <section className="bg-[#2f3951] py-5 md:py-5">
      <div className="container mx-auto px-6 md:px-8 lg:px-12">
        {/* Breadcrumb */}
        <nav className="flex items-center text-white/80 text-sm mb-3">
          <Link to="/" className="flex items-center hover:text-[#ca9a37] transition-colors">
            <Home className="w-4 h-4 mr-1" />
            Home
          </Link>
          <ChevronRight className="w-4 h-4 mx-2" />
          <span className="text-[#ca9a37]">Loan Products</span>
        </nav>

        {/* Title */}
        {heroData?.Title && (
          <h1 className="text-2xl md:text-4xl font-bold text-white mb-2">
            {heroData.Title}
          </h1>
        )}
        <div className="w-24 h-1 bg-[#ca9a37] mb-3"></div>

        {heroData?.Description && (
          <p className="text-base text-gray-200 max-w-2xl">
            {heroData.Description}
          </p>
        )}
      </div>
    </section>
  );
};

export default LoanProductsHero;
